import * as THREE from "three";
import Experience from "../../../Experience.js";

export default class Caustics {
    constructor(bounds) {
        this.experience = new Experience();
        this.noiseTexture = this.experience.noiseTexture;
        this.worleyTexture = this.experience.worleyTexture;

        this.uniforms = {
            uTime: { value: 0 },
            uWarpFrequency: { value: 1 },
            uWarpAmp: { value: 0.04 },
            uCausticScale: { value: 0.02 },
            uCausticStrength: { value: 35.0 },
        };

        this.injectShaderChunks();

        // Debug
        if (this.experience.debug.active) {
            this.debugFolder = this.experience.debug.ui.addFolder("Caustics");

            this.debugFolder
                .add(this.uniforms.uWarpFrequency, "value")
                .min(0)
                .max(10)
                .step(0.01)
                .name("Warp Frequency");

            this.debugFolder
                .add(this.uniforms.uWarpAmp, "value")
                .min(0)
                .max(2)
                .step(0.01)
                .name("Warp Amp");

            this.debugFolder
                .add(this.uniforms.uCausticScale, "value")
                .min(0)
                .max(0.2)
                .step(0.001)
                .name("Caustic Scale");

            this.debugFolder
                .add(this.uniforms.uCausticStrength, "value")
                .min(0)
                .max(50)
                .step(0.01)
                .name("Caustic Strength");
        }
    }

    replaceLastInstance(text, target, replacement) {
        const index = text.lastIndexOf(target);

        if (index === -1) return text; // Target not found

        return (
            text.slice(0, index) +
            replacement +
            text.slice(index + target.length)
        );
    }

    injectShaderChunks() {
        // Ensure global caustics are only injected once
        if (THREE.ShaderChunk.common.includes("vCausticWorldPosition")) return;

        THREE.ShaderChunk.common += `
        #ifdef USE_CAUSTICS
        uniform float uTime;
        uniform float uWarpFrequency;
        uniform float uCausticStrength;
        uniform float uCausticScale;
        uniform float uWarpAmp;
        uniform sampler2D uNoiseTexture;
        uniform sampler2D uWorleyTexture;
        varying vec3 vCausticWorldPosition;

        float getCaustic(vec2 p, float t) {
            vec3 warp = texture2D(uNoiseTexture, p * uWarpFrequency - vec2(t * 0.04, t * -0.05)).rgb;
            float noiseG = texture2D(uWorleyTexture, p + warp.g * uWarpAmp + vec2(t * 0.04, t * -0.05)).g;
            noiseG = pow(noiseG, 4.0);
            float noise = noiseG * warp.b;
            return noise;
        }
        float getSurfaceCaustic(vec2 p, float t) {
            vec3 warp = texture2D(uNoiseTexture, p * 0.6 - vec2(t * 0.04, t * -0.05)).rgb;
            float noiseG = texture2D(uWorleyTexture, p + warp.g * 0.1 + vec2(t * 0.04, t * -0.05)).g;
            noiseG = pow(noiseG, 6.0);
            float noise = noiseG * warp.b;
            return noise;
        }
        #endif
        `;

        THREE.ShaderChunk.worldpos_vertex += `
        #ifdef USE_CAUSTICS
        vec4 globalCausticWorldPos = vec4( transformed, 1.0 );
        #ifdef USE_INSTANCING
            globalCausticWorldPos = instanceMatrix * globalCausticWorldPos;
        #endif
        globalCausticWorldPos = modelMatrix * globalCausticWorldPos;
        vCausticWorldPosition = globalCausticWorldPos.xyz;
        #endif
        `;

        THREE.ShaderChunk.lights_fragment_begin = this.replaceLastInstance(
            THREE.ShaderChunk.lights_fragment_begin,
            "RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );",
            `#ifdef USE_CAUSTICS
            float causticPattern = getCaustic(vCausticWorldPosition.xz * uCausticScale, uTime * 0.001);
            directLight.color *= (1.0 + causticPattern * uCausticStrength);
            #endif
            RE_Direct( directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
            `,
        );
    }

    applyCaustics(material) {
        if (!material.defines) material.defines = {};
        material.defines.USE_CAUSTICS = "";

        // Preserve any existing onBeforeCompile function
        const origOnBeforeCompile = material.onBeforeCompile;
        material.onBeforeCompile = (shader, renderer) => {
            // inject caustics uniforms on top of the original onBeforeCompile insertions
            shader.uniforms.uTime = this.uniforms.uTime;
            shader.uniforms.uWarpFrequency = this.uniforms.uWarpFrequency;
            shader.uniforms.uWarpAmp = this.uniforms.uWarpAmp;
            shader.uniforms.uCausticStrength = this.uniforms.uCausticStrength;
            shader.uniforms.uCausticScale = this.uniforms.uCausticScale;
            shader.uniforms.uNoiseTexture = { value: this.noiseTexture };
            shader.uniforms.uWorleyTexture = { value: this.worleyTexture };

            if (origOnBeforeCompile) {
                origOnBeforeCompile.call(material, shader, renderer);
            }
        };
        material.needsUpdate = true;
    }

    update() {
        this.uniforms.uTime.value = this.experience.time.elapsed;
    }
}
